import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLGDetection } from '../../LG/hooks/useLGDetection'

/**
 * Hook for managing LG TV specific upload behavior including detection,
 * conditional rendering, and navigation handling
 *
 * @returns {Object} Hook interface containing LG TV state and handlers
 */
const useLGUpload = () => {
  const navigate = useNavigate()
  const { isLGSmartTV } = useLGDetection()

  /**
   * Detect if current device is an LG Smart TV
   * Memoized to prevent unnecessary re-computations
   * YOU CAN TEMPORARILY FORCED TO TRUE FOR TESTING
   */
  const isLGTV = useMemo(() => {
    // ONLY FOR TESTING: Force LG TV mode, comment out return isLGSmartTV, uncomment return true
    // return true
    return isLGSmartTV
  }, [isLGSmartTV])

  /**
   * Handles navigation back to gallery from LG TV
   */
  const handleBackToGallery = useCallback(() => {
    navigate('/gallery')
  }, [navigate])

  /**
   * Determines if upload dashboard should be shown
   * @returns {boolean} True if dashboard should be rendered
   */
  const shouldShowUploadDashboard = useCallback(() => {
    return !isLGTV
  }, [isLGTV])

  /**
   * Determines if LG TV video should be shown
   * @returns {boolean} True if LG video should be rendered
   */
  const shouldShowLGVideo = useCallback(() => {
    return isLGTV
  }, [isLGTV])

  /**
   * Gets LG TV specific configuration
   * @returns {Object} LG TV configuration object
   */
  const getLGTVConfig = useCallback(() => {
    return {
      isLGTV,
      videoConfig: {
        width: '100%',
        height: 'auto',
        autoPlay: true,
        controls: true,
        loop: true
      },
      buttonConfig: {
        className: 'back-to-gallery-btn',
        text: 'Back to Gallery',
        onClick: handleBackToGallery
      }
    }
  }, [isLGTV, handleBackToGallery])

  /**
   * Gets upload dashboard configuration based on device type
   * @returns {Object} Dashboard configuration object
   */
  const getUploadDashboardConfig = useCallback(() => {
    return {
      shouldRender: shouldShowUploadDashboard(),
      containerClass: 'upload-container',
      dashboardConfig: {
        disableInformer: false,
        proudlyDisplayPoweredByUppy: true,
        height: 400,
        plugins: ['MyDevice', 'Dropbox'],
        disableThumbnailGenerator: false
      }
    }
  }, [shouldShowUploadDashboard])

  /**
   * Handles LG TV specific upload restrictions or modifications
   * @param {Object} uppyOptions - Base Uppy configuration
   * @returns {Object} Modified Uppy configuration for LG TV
   */
  const applyLGTVUploadConfig = useCallback(
    uppyOptions => {
      if (!isLGTV) {
        return uppyOptions
      }

      // Apply LG TV specific modifications if needed
      return {
        ...uppyOptions,
        // Add any LG TV specific restrictions or configurations
        restrictions: {
          ...uppyOptions.restrictions
          // Could add LG TV specific file size limits, etc.
        }
      }
    },
    [isLGTV]
  )

  /**
   * Gets device-specific CSS classes
   * @returns {Object} CSS classes for different elements
   */
  const getDeviceClasses = useCallback(() => {
    return {
      notificationWrapper: 'notification-wrapper',
      lgVideo: 'lg-video',
      uploadContainer: 'upload-container',
      backButton: 'back-to-gallery-btn'
    }
  }, [])

  /**
   * Handles device-specific error handling
   * @param {Error} error - Error object
   * @param {string} context - Context where error occurred
   */
  const handleDeviceError = useCallback(
    (error, context) => {
      console.error(`LG TV Upload Error in ${context}:`, error)

      if (isLGTV) {
        // Handle LG TV specific error behavior
        // Could show different error messages or fallback behavior
        console.warn('LG TV specific error handling applied')
      }
    },
    [isLGTV]
  )

  return {
    isLGTV,
    shouldShowUploadDashboard,
    shouldShowLGVideo,
    handleBackToGallery,
    getLGTVConfig,
    getUploadDashboardConfig,
    applyLGTVUploadConfig,
    getDeviceClasses,
    handleDeviceError
  }
}

export default useLGUpload
